import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";

type Role = "admin" | "editor" | "viewer";

interface Props {
  children: React.ReactNode;
  /** Perfis autorizados a acessar a rota. Se omitido, qualquer sessão válida entra. */
  requiredRole?: Role[];
  /** Restringe a rota a e-mails @razconsulting.com.br (aba interna Raz). */
  razEmailOnly?: boolean;
}

export default function ProtectedRoute({ children, requiredRole, razEmailOnly }: Props) {
  const { session, isLoading } = useAuth();
  const [, navigate] = useLocation();

  const isRazAdmin = session?.isRazAdmin ?? false;
  const role = session?.role ?? "viewer";
  const isRazEmail = (session?.email ?? "").toLowerCase().trim().endsWith("@razconsulting.com.br");
  // HU-002: raz admin tem acesso total; senão, valida o perfil exigido
  const authorized = razEmailOnly
    ? isRazEmail
    : (!requiredRole || isRazAdmin || requiredRole.includes(role as Role));

  useEffect(() => {
    if (!isLoading && !session) {
      navigate("/login");
    }
  }, [isLoading, session, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="w-6 h-6 border-2 border-zinc-600 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) return null;

  // HU-002: sessão válida mas sem permissão para esta tela
  if (!authorized) {
    return (
      <div className="h-full min-h-screen flex flex-col items-center justify-center gap-4 bg-[#F5F4FF] p-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#FDE8F5" }}>
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="#BE185D" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div className="text-center">
          <h1 className="text-[#1E1B4B] font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif" }}>
            Acesso restrito
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Esta área é exclusiva para administradores.
          </p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 rounded-lg text-white text-sm font-semibold"
          style={{ background: "linear-gradient(90deg,#422BFF,#605BE5)" }}
        >
          Voltar ao início
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
